import formatFloat from '../../api/getChartSeries/formatFloat';
import tinyColor from 'tinycolor2';

import { ChartColors } from '../../theme/tokens';
import getAllValuesFor from '../../getAllValuesFor';
import { findSeriesDef, findSeriesDefIndex } from './seriesDataKey';
import isCustomRangeInput from '../../isCustomRangeInput';
import formatDateLabel from './formatDateLabel';
import {
  getIsLastDatePartial,
  removePartialIntervalsDates,
} from '../../removePartialIntervals';
import moment from 'moment';
import base from './highchartOptions/baseOptions';
import buildTooltipFormatter from './tooltipFormatter';
import computeTrendLine from './computeTrendLine';
import getOrderedDaysOfWeek from '../../hooks/useMetricMatrix/getOrderedDaysOfWeek';
import mapGraphqlDayNumberToString from '../../mapGraphqlDayNumberToString';
import metricTypeCheckers from '../../types/metricTypeCheckers';
import isDefined from '../../isDefined';

const ORDERED_HOURS = [
  '00',
  '01',
  '02',
  '03',
  '04',
  '05',
  '06',
  '07',
  '08',
  '09',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
];

// Series data is keyed by seriesDataKey: the metric id, or the series id for
// a series with query overrides
const getSeriesDef = findSeriesDef;
const getSeriesDefIndex = findSeriesDefIndex;

const getYAxisNumberFor = (
  seriesDef: V5ChartDefinitionSeries | undefined,
  allSeries: V5ChartDefinitionSeries[],
) => {
  if (!seriesDef || !seriesDef.plotOnSeparateAxis) {
    return 0;
  }

  const seriesAxisLookup: { [seriesId: string]: number | undefined } = {};
  let current = 1;
  allSeries.forEach((series, index) => {
    if (index === 0) {
      seriesAxisLookup[series.id] = 0;
    } else if (series.plotOnSeparateAxis) {
      seriesAxisLookup[series.id] = current;
      current++;
    }
  });

  return seriesAxisLookup[seriesDef.id] || 0;
};

const getSeriesTypeFor = (seriesDef: V5ChartDefinitionSeries | undefined) => {
  if (!seriesDef) {
    return 'column';
  }

  return seriesDef.type;
};

const getIsOppositeFor = (seriesDef: V5ChartDefinitionSeries | undefined) => {
  if (!seriesDef) {
    return undefined;
  }

  return seriesDef.opposite;
};

const getSeriesFor = ({
  data,
  chartDef,
  isCombo,
  dateScope,
  autoInterval,
  filterInput,
  weekStartsOn,
  colors,
}: {
  data: V5ChartData;
  chartDef: V5ChartDefinition;
  isCombo: boolean;
  dateScope?: DateRangeInput;
  autoInterval?: AutoInterval;
  filterInput?: FilterInput;
  weekStartsOn: WeekStartsOn;
  colors: string[];
}): BarSeriesItem[] => {
  const rawMetricSeries = Object.entries(data).map(
    ([seriesKey, { name, metric, response: baseResponse }]) => {
      const seriesDef = getSeriesDef(chartDef, seriesKey);
      const seriesDefIndex = getSeriesDefIndex(chartDef, seriesKey);
      const yAxis = getYAxisNumberFor(seriesDef, chartDef.series);
      const type = getSeriesTypeFor(seriesDef);
      const metricIds = (() => {
        if (metricTypeCheckers.isCompoundMetric(metric)) {
          return metric.metricIds;
        }

        return [metric.id];
      })();

      const response = chartDef.excludePartialIntervals
        ? responseWithoutPartialInterval(
            baseResponse,
            dateScope,
            autoInterval
              ? autoInterval.interval
              : chartDef.trendByCalendarInterval,
            filterInput,
          )
        : baseResponse;

      const marker =
        type === 'line'
          ? {
              enabled: false,
              symbol: 'circle',
              fillColor: 'var(--color-bg-primary)',
              lineWidth: 1,
              lineColor: colors[seriesDefIndex],
              radius: 3,
            }
          : undefined;

      const isDayOfWeek =
        chartDef.dimensionA && chartDef.dimensionA.isGroupByDayOfWeek;
      const data = (() => {
        const responseToUse = (() => {
          if (chartDef.trendByHourInterval) {
            return ORDERED_HOURS.map((hour) => {
              return response.find((r) => r['key'].toString() === hour);
            }).map((r) => {
              if (r) {
                return r;
              }
              return {};
            });
          }

          if (!isDayOfWeek) {
            return response;
          }
          const orderedDays = getOrderedDaysOfWeek(weekStartsOn);
          return orderedDays
            .map((day) => {
              // Rows from another grouping (e.g. while a changed chart
              // refetches) have no day
              return response.find(
                (r) =>
                  r['dayOfWeek'] !== undefined &&
                  mapGraphqlDayNumberToString(r['dayOfWeek'].toString()) ===
                    day,
              );
            })
            .map((r) => (r ? r : {}));
          // Return an empty obj to ensure the axis aligns in partial weeks
        })();

        return Object.values(responseToUse).map((i) =>
          formatFloat(i[metric.id], metric.formatting.precision),
        );
      })();

      return {
        name,
        data,
        metric,
        metricId: metric.id,
        seriesKey,
        queryOverrides: seriesDef ? seriesDef.queryOverrides : undefined,
        marker,
        prefix: metric.formatting.prefix,
        postfix: metric.formatting.postfix,
        interval: chartDef.trendByCalendarInterval,
        colorIndex: seriesDefIndex,
        yAxis: isCombo ? yAxis : undefined,
        type,
        id: seriesKey,
        metricIds,
      } as BarSeriesItem;
    },
  );

  const averageSeries = Object.entries(data)
    .filter(([seriesKey]) => {
      const seriesDef = getSeriesDef(chartDef, seriesKey);
      if (!seriesDef) {
        return false;
      }
      return seriesDef.plotAverage;
    })
    .map(([seriesKey, { name, metric, response: baseResponse }]) => {
      const seriesDef = getSeriesDef(chartDef, seriesKey);
      const seriesDefIndex = getSeriesDefIndex(chartDef, seriesKey);
      const yAxis = getYAxisNumberFor(seriesDef, chartDef.series);
      const response = chartDef.excludePartialIntervals
        ? responseWithoutPartialInterval(
            baseResponse,
            dateScope,
            autoInterval
              ? autoInterval.interval
              : chartDef.trendByCalendarInterval,
            filterInput,
          )
        : baseResponse;
      const sum = response.reduce(
        (
          accumulator: number,
          currentValue: { [key: string]: string | number | FilterInput },
        ) => accumulator + (currentValue[metric.id] as number),
        0,
      );
      const average = formatFloat(
        sum / response.length,
        metric.formatting.precision,
      );
      const metricIds = (() => {
        if (metricTypeCheckers.isCompoundMetric(metric)) {
          return metric.metricIds;
        }

        return [metric.id];
      })();

      const marker = {
        enabled: false,
        symbol: 'circle',
        fillColor: 'var(--color-bg-primary)',
        lineWidth: 1,
        lineColor: colors[seriesDefIndex],
        radius: 3,
      };

      return {
        // A series with query overrides is named for them
        name: `${seriesKey === metric.id ? metric.name : name} (average)`,
        type: 'line',
        metric,
        metricId: metric.id,
        seriesKey,
        prefix: metric.formatting.prefix,
        postfix: metric.formatting.postfix,
        interval: chartDef.trendByCalendarInterval,
        yAxis: isCombo ? yAxis : undefined,
        dashStyle: 'dash',
        showInLegend: true,
        linkedTo: seriesKey,
        role: 'average',
        data: response.map(() =>
          formatFloat(average, metric.formatting.precision),
        ),
        colorIndex: seriesDefIndex,
        metricIds,
        marker,
      } as BarSeriesItem;
    });

  const trendSeries = Object.entries(data)
    .filter(([seriesKey]) => {
      const seriesDef = getSeriesDef(chartDef, seriesKey);
      if (!seriesDef) {
        return false;
      }
      return seriesDef.plotTrend;
    })
    .map(([seriesKey, { name, metric, response: baseResponse }]) => {
      const seriesDef = getSeriesDef(chartDef, seriesKey);
      const yAxis = getYAxisNumberFor(seriesDef, chartDef.series);
      // Trend always ignores partial intervals: a half-finished first/last
      // bucket reads as artificially low and skews the line.
      const filteredResponse = responseWithoutPartialInterval(
        baseResponse,
        dateScope,
        autoInterval ? autoInterval.interval : chartDef.trendByCalendarInterval,
        filterInput,
      );
      const displayResponse = chartDef.excludePartialIntervals
        ? filteredResponse
        : baseResponse;

      const trendValues = computeTrendLine({
        values: filteredResponse.map((row) => row[metric.id] as number),
      });

      // Align trend output indices with the bars on the x-axis. When the
      // bars include partial buckets but the trend ignores them, write null
      // (Highcharts gap) at the dropped positions.
      const startDropped =
        filteredResponse.length > 0 && filteredResponse[0] !== baseResponse[0];
      const offset =
        displayResponse === filteredResponse || !startDropped ? 0 : 1;
      const data: (number | null)[] = displayResponse.map((_value, index) => {
        const trendIndex = index - offset;
        if (trendIndex < 0 || trendIndex >= trendValues.length) {
          return null;
        }
        const trendValue = trendValues[trendIndex];
        if (!Number.isFinite(trendValue)) {
          return null;
        }
        // Don't round to the metric's display precision — rounding each point
        // turns a sloped line into a visible staircase for low-precision
        // metrics. Tooltips/axis labels apply precision via their own formatters.
        return trendValue;
      });
      const metricIds = (() => {
        if (metricTypeCheckers.isCompoundMetric(metric)) {
          return metric.metricIds;
        }

        return [metric.id];
      })();

      const marker = {
        enabled: false,
        symbol: 'circle',
        fillColor: 'var(--color-bg-primary)',
        lineWidth: 1,
        lineColor: 'orange',
        radius: 3,
      };

      return {
        name: seriesDef ? `${name} (trend)` : undefined,
        type: 'line',
        metric,
        metricId: metric.id,
        seriesKey,
        prefix: metric.formatting.prefix,
        postfix: metric.formatting.postfix,
        interval: chartDef.trendByCalendarInterval,
        yAxis: isCombo ? yAxis : undefined,
        dashStyle: 'dash',
        showInLegend: true,
        linkedTo: seriesKey,
        role: 'trend',
        data,
        color: 'orange',
        metricIds,
        marker,
      } as BarSeriesItem;
    });

  return [...rawMetricSeries, ...averageSeries, ...trendSeries];
};

const getHasAnySeparateAxis = (chartDef: V5ChartDefinition) => {
  const { series } = chartDef;
  return series.some((s) => s.plotOnSeparateAxis);
};

const getSeriesColor = ({
  index,
  chartDef,
  colors,
}: {
  index: number;
  chartDef: V5ChartDefinition;
  colors: string[];
}) => {
  if (!getHasAnySeparateAxis(chartDef)) {
    return 'var(--color-text-primary)';
  }
  return tinyColor(colors[index]).saturate(20).toHexString();
};

const getYAxisFor = (
  series: BarSeriesItem[],
  chartDef: V5ChartDefinition,
  fontSize: number,
  isCombo: boolean,
  colors: string[],
) => {
  const getMinRangeFor = (
    seriesDef: V5ChartDefinitionSeries | undefined,
    data: (number | null)[],
  ): number | undefined => {
    if (seriesDef?.minRangePercent === undefined) {
      return undefined;
    }
    const validValues = data.filter(
      (v): v is number => v !== null && !isNaN(v),
    );
    if (validValues.length === 0) {
      return undefined;
    }
    const maxVal = Math.max(...validValues);
    if (maxVal <= 0) {
      return undefined;
    }
    return maxVal * (seriesDef.minRangePercent / 100);
  };

  if (isCombo) {
    const usedSeriesItems: V5ChartDefinitionSeries[] = [];
    return series
      .map((s, index) => {
        const seriesDef = getSeriesDef(chartDef, s.seriesKey || s.metricId);
        if (!seriesDef) {
          return undefined;
        }
        if (index !== 0 && !seriesDef.plotOnSeparateAxis) {
          return undefined;
        }
        if (usedSeriesItems.some((s) => s.id === seriesDef.id)) {
          return undefined;
        }
        usedSeriesItems.push(seriesDef);
        return {
          max: seriesDef.maxY,
          min: seriesDef.minY,
          opposite: seriesDef.opposite,
          minRange: getMinRangeFor(seriesDef, s.data),
        };
      })
      .filter(isDefined);
  }

  return series.map((s) => {
    const seriesDef = getSeriesDef(chartDef, s.seriesKey || s.metricId);

    const n = getYAxisNumberFor(seriesDef, chartDef.series);
    return {
      title: {
        text: getHasAnySeparateAxis(chartDef) ? s.metric.name : '',
        style: {
          fontSize,
          color: getSeriesColor({ index: n, chartDef, colors }),
        },
        enabled: false,
      },
      labels: {
        style: {
          fontSize,
          color: getSeriesColor({ index: n, chartDef, colors }),
        },
      },
      max: seriesDef?.maxY,
      min: seriesDef?.minY,
      opposite: getIsOppositeFor(seriesDef),
      minRange: getMinRangeFor(seriesDef, s.data),
    };
  });
};

const responseWithoutPartialInterval = (
  response: { [key: string]: string | number | FilterInput }[],
  dateScope?: DateRangeInput,
  interval?: FleetOps.Interval,
  filterInput?: FilterInput,
): { [key: string]: string | number | FilterInput }[] => {
  if (response.length === 0) {
    return response;
  }
  // Last item
  const lastItem = response[response.length - 1];
  const lastItemDate = lastItem ? lastItem['date'] : undefined;
  if (!lastItemDate || typeof lastItemDate === 'object') {
    return response;
  }

  const isLastItemPartial = getIsLastDatePartial({
    date: lastItemDate,
    dateScope,
    interval,
  });

  // First item
  const firstItemDate = response[response.length - 1]['date'] as string;
  if (!firstItemDate) {
    return response;
  }

  const isFirstItemPartial = (() => {
    if (!interval || interval === 'auto' || !filterInput || !dateScope) {
      return false;
    }

    const dateRangeStart = dateScope.startDate;
    const firstItemDate = response[0]['date'] as string;
    return moment.utc(firstItemDate).isBefore(moment.utc(dateRangeStart));
  })();

  if (isFirstItemPartial && isLastItemPartial) {
    return response.slice(1, response.length - 1);
  } else if (isFirstItemPartial) {
    return response.slice(1);
  } else if (isLastItemPartial) {
    return response.slice(0, response.length - 1);
  } else {
    return response;
  }
};

const toBarSeries = (
  data: V5ChartData,
  chartDef: V5ChartDefinition,
  isCombo: boolean,
  unitsLocale: string,
  dateScope: DateRangeInput,
  fontSize: number,
  weekStartsOn: WeekStartsOn,
  formatMetric: (args: {
    metricId: string;
    value: string | number | null | undefined;
  }) => string,
  autoInterval?: AutoInterval,
  // The colours the chart draws with (see ChartPaletteContext)
  colors: string[] = ChartColors,
): BarSeries => {
  const filterInput = (() => {
    try {
      return Object.values(data)[0].response[0].filterInput as FilterInput;
    } catch (ex) {
      return undefined;
    }
  })();
  const series = getSeriesFor({
    data,
    chartDef,
    isCombo,
    dateScope,
    autoInterval,
    weekStartsOn,
    filterInput,
    colors,
  });
  const yAxis = getYAxisFor(series, chartDef, fontSize, isCombo, colors);

  if (chartDef.groupByDayOfWeek) {
    const categories = getOrderedDaysOfWeek(weekStartsOn);
    return {
      xAxis: {
        categories,
        labels: {
          style: {
            fontSize,
          },
        },
      },
      series,
      yAxis,
    };
  }

  if (chartDef.trendByHourInterval) {
    return {
      xAxis: {
        categories: ORDERED_HOURS,
        labels: {
          style: {
            fontSize,
          },
        },
      },
      series,
      yAxis,
    };
  }

  if (chartDef.trendByCalendarInterval || chartDef.trendByFixedIntervalDays) {
    const hasDateAxis =
      chartDef.trendByCalendarInterval &&
      (chartDef.trendByCalendarInterval !== 'auto' || !!autoInterval);

    const allDates = getAllValuesFor(data, 'date');
    const interval = autoInterval
      ? autoInterval.interval
      : chartDef.trendByCalendarInterval;
    const dateScope = (() => {
      try {
        return Object.values(data)[0].response[0].dateScope as DateRangeInput;
      } catch (ex) {
        return undefined;
      }
    })();
    const categories = chartDef.excludePartialIntervals
      ? removePartialIntervalsDates(allDates, interval, dateScope)
      : allDates;

    const isComboLine =
      isCombo && chartDef.series.some((s) => s.type === 'line');
    return {
      xAxis: {
        labels: {
          style: {
            whiteSpace: hasDateAxis ? 'nowrap' : undefined,
            fontSize,
          },
          step: Math.ceil(series[0] ? series[0].data.length / 7 : 2),
          autoRotation: hasDateAxis ? 0 : undefined,
          formatter: function () {
            if (hasDateAxis && chartDef.trendByCalendarInterval) {
              if (isComboLine) {
                const tickInterval = autoInterval
                  ? autoInterval.interval
                  : chartDef.trendByCalendarInterval;
                const m =
                  tickInterval === 'week'
                    ? moment.utc(this.value).add(6, 'd')
                    : moment.utc(this.value);
                return m.format(tickInterval === 'hour' ? 'MM/DD ha' : 'MM/DD');
              } else {
                // @ts-ignore
                return formatDateLabel(
                  this.value,
                  autoInterval
                    ? autoInterval.interval
                    : chartDef.trendByCalendarInterval,
                  true,
                  undefined,
                  undefined,
                  undefined,
                  true,
                );
              }
            }
            // @ts-ignore
            return this.value;
          },
        },
        categories,
      },
      tooltip:
        isComboLine && series.length === 1
          ? {
              ...base(formatMetric).tooltip,
              useHTML: true,
              backgroundColor: 'var(--color-bg-primary)',
              borderColor: 'var(--color-border-primary)',
              style: { color: 'var(--color-text-primary)' },
              // @ts-ignore
              formatter: buildTooltipFormatter({
                series,
                autoInterval,
                data,
                formatMetric,
              }),
            }
          : {
              ...base(formatMetric).tooltip,
              useHTML: true,
              formatter: function () {
                // @ts-ignore
                const ctx = this as {
                  x: string | number;
                  points?: {
                    color: string;
                    y: number;
                    series: { name: string; userOptions: { metricId: string } };
                  }[];
                };
                const headerInterval = hasDateAxis
                  ? autoInterval
                    ? autoInterval.interval
                    : chartDef.trendByCalendarInterval
                  : undefined;
                const headerText = headerInterval
                  ? formatDateLabel(ctx.x, headerInterval, true)
                  : String(ctx.x);
                const pointsHtml = (ctx.points || [])
                  .map((p) => {
                    const value = formatMetric({
                      metricId: p.series.userOptions.metricId,
                      value: p.y,
                    });
                    return `<span style="color:${p.color}">● </span><b><span style="color: var(--color-text-muted);">${p.series.name}</span><b> ${value}</b><br/>`;
                  })
                  .join('');
                return `<b>${headerText}</b><br/>${pointsHtml}`;
              },
            },
      series,
      yAxis: isCombo
        ? yAxis
        : yAxis.map((a) => ({
            ...a,
            title: { enabled: false },
          })),
    };
  }
  if (chartDef.dimensionA) {
    if (chartDef.dimensionA.rangeInput) {
      const groupByField = 'key';
      if (isCustomRangeInput(chartDef.dimensionA.rangeInput)) {
        const categories = getAllValuesFor(data, groupByField);
        return {
          xAxis: {
            categories,
            labels: {
              style: {
                fontSize,
              },
            },
          },
          series: series,
          yAxis: isCombo
            ? yAxis
            : yAxis.map((a) => ({
                ...a,
                title: { enabled: false },
                labels: {
                  style: {
                    fontSize,
                  },
                },
              })),
        };
      } else {
        const categories = getAllValuesFor(data, groupByField);
        const interval = chartDef.dimensionA.rangeInput.fixed;
        return {
          xAxis: {
            categories: categories.map((c, index) => {
              return `${index * interval} - ${(index + 1) * interval}`;
            }),
            labels: {
              style: {
                fontSize,
              },
            },
          },
          series: series,
          yAxis: isCombo
            ? yAxis
            : yAxis.map((a) => ({
                ...a,
                title: { enabled: false },
                labels: {
                  style: {
                    fontSize,
                  },
                },
              })),
        };
      }
    } else {
      const groupByField = chartDef.dimensionA.field;
      const categories = getAllValuesFor(data, groupByField);
      return {
        xAxis: {
          categories,
          labels: {
            style: {
              fontSize,
            },
          },
        },
        series: series,
        yAxis: isCombo
          ? yAxis
          : yAxis.map((a) => ({
              ...a,
              title: { enabled: false },
              labels: {
                style: {
                  fontSize,
                },
              },
            })),
      };
    }
  }

  return {
    xAxis: {
      categories: [],
      labels: {},
    },
    series: [],
    yAxis: [],
  };
};

export default toBarSeries;
